import { useCallback, useEffect, useRef, useState } from 'react';

export const useInfiniteScroll = () => {
    const [pageToRequest, setPageToRequest] = useState(1);
    const observerRef = useRef(null);
    const latestEntryRef = useRef<IntersectionObserverEntry | null>(null);

    const resetPageToRequest = () => {
        setPageToRequest(1);
    };

    // if the users screen is big, he might not be able to scroll down to trigger the next page request
    // so this function is needed to manually check if next page should be requested, even if no scroll is made
    const checkPageToRequest = useCallback(() => {
        if (latestEntryRef.current?.isIntersecting) {
            setPageToRequest((prevPage) => prevPage + 1);
        }
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                // Store the latest entry to be used by checkPageToRequest
                latestEntryRef.current = entries[0];
                if (entries[0].isIntersecting) {
                    setPageToRequest((prevPage) => prevPage + 1);
                }
            },
            { threshold: 1 }
        );

        const elem = observerRef.current;

        if (elem) {
            observer.observe(elem);
        }

        // cleanup
        return () => {
            if (elem) {
                observer.unobserve(elem);
            }
        };
    }, []);

    return { pageToRequest, resetPageToRequest, checkPageToRequest, observerRef };
};
